import {otsBaseApi} from "../../ots-api";
import {CustomFieldProfileItem} from "../../../model/custom-field-profile-item";

const ALL_TAG_TYPES = [
  "CustomFieldProfileItems",
  "ProfileCustomFieldProfileItems",
  "CompanyCustomFieldProfileItems",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const customFieldProfileItemApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCustomFieldProfileItems: builder.query<
      CustomFieldProfileItem[],
      void
    >({
      query: () => ({
        url: `/custom-field-profile-items/all`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldProfileItems", id} as const)),
            {type: "CustomFieldProfileItems", id: "LIST"}
          ];
        } else {
          return [{type: "CustomFieldProfileItems", id: "LIST"}];
        }
      },
    }),
    getCustomFieldProfileItemsByProfile: builder.query<
      CustomFieldProfileItem[],
      string
    >({
      query: (arg) => ({
        url: `/custom-field-profile-items/profile/${arg}`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldProfileItems", id} as const)),
            {type: "ProfileCustomFieldProfileItems", id: arg}
          ];
        } else {
          return [{type: "ProfileCustomFieldProfileItems", id: arg}];
        }
      },
    }),
    getActiveCustomFieldProfileItemsByCompany: builder.query<
      CustomFieldProfileItem[],
      { companyId: string, objectType: string, dttm: number }
    >({
      query: ({companyId, objectType, dttm}) => ({
        url: `/custom-field-profile-items/company/${companyId}`,
        method: 'GET',
        params: {
          objectType: objectType,
          dttm: dttm
        }
      }),
      providesTags: (result, _error, { companyId, objectType, dttm }) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldProfileItems", id} as const)),
            {type: "CompanyCustomFieldProfileItems", id: `${companyId}:${objectType}:${dttm}`},
          ];
        } else {
          return [{type: "CompanyCustomFieldProfileItems", id: `${companyId}:${objectType}:${dttm}`}];
        }
      },
    }),
    getCustomFieldProfileItem: builder.query<CustomFieldProfileItem, string>({
      query: (id) => ({
        url: `/custom-field-profile-items/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "CustomFieldProfileItems", id}]
    }),
    createCustomFieldProfileItem: builder.mutation<CustomFieldProfileItem, Partial<CustomFieldProfileItem>>({
      query: (mapping) => ({
        url: "/custom-field-profile-items",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: (result) => {
        const tags: { type: TagType, id?: string }[] = []
        tags.push({type: "CustomFieldProfileItems", id: "LIST"})

        if (result?.customFieldProfileId) {
          tags.push({type: "ProfileCustomFieldProfileItems", id: result.customFieldProfileId})
        }

        return tags
      }
    }),
    updateCustomFieldProfileItem: builder.mutation<CustomFieldProfileItem, Partial<CustomFieldProfileItem>>({
      query: (mapping) => ({
        url: "/custom-field-profile-items",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "CustomFieldProfileItems", id},
        {type: "ObjectObjectChangeLogs", id},
        {type: "ProfileCustomFieldProfileItems"},
      ]
    }),
    deleteCustomFieldProfileItem: builder.mutation<void, Partial<CustomFieldProfileItem>>({
      query: (mapping) => ({
        url: "/custom-field-profile-items",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "CustomFieldProfileItems", id},
        {type: "ProfileCustomFieldProfileItems"},
      ]
    }),
    patchCustomFieldProfileItemsByProfile: builder.mutation<CustomFieldProfileItem[], {
      profileId: string,
      items: Partial<CustomFieldProfileItem>[]
    }>({
      query: ({profileId, items}) => ({
        url: `/custom-field-profiles/${profileId}/items`,
        method: "PATCH",
        body: items,
      }),
      invalidatesTags: (result) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "CustomFieldProfileItems", id: "LIST"})

        if (result) {
          for (const item of result) {
            tags.push({type: "CustomFieldProfileItems", id: item.id})
            tags.push({type: "ObjectObjectChangeLogs", id: item.id})
            tags.push({type: "ProfileCustomFieldProfileItems", id: item.customFieldProfileId})
          }
        }

        return tags
      }
    }),
  })
});

export const {
  useGetAllCustomFieldProfileItemsQuery,
  useGetCustomFieldProfileItemsByProfileQuery,
  useGetActiveCustomFieldProfileItemsByCompanyQuery,
  useGetCustomFieldProfileItemQuery,
  useCreateCustomFieldProfileItemMutation,
  useUpdateCustomFieldProfileItemMutation,
  useDeleteCustomFieldProfileItemMutation,
  usePatchCustomFieldProfileItemsByProfileMutation,
} = customFieldProfileItemApi;
